/**
 * EngagementProgressingPage
 *
 * "Work in progress summary period-ended engagements report" -- matches
 * the uploaded screenshot/spreadsheet's column layout. Real (built from
 * SSS `/schedules` + `/engagements` + `/clients` + `/staff` +
 * `/position-rates`, and TRS `/post-times`) so far:
 *   - "Booked SSS" Total Hours + Total time charges (SSS staff schedule
 *     rows, charges = hours x each staff's position rate)
 *   - "Approved budget" Total Hours + Total budget (engagement's
 *     `budget_hours`/`budget_fee` -- both confirmed real field names)
 *   - "EL Approved" Fee (engagement's `budget_el` -- unconfirmed field
 *     name, best-guess pending verification)
 *   - "Approved TRS" Total Hours + Total time charges and "Pending
 *     Approval TRS" Total Hours + Total time charges -- both from TRS
 *     `/post-times`, split by the REAL, confirmed `approval_status`
 *     field (pending/approved/rejected), filtered to `job_type ===
 *     'client'` entries only (see useTrsTimeEntries.js's docblock).
 *     An earlier pass here used two undocumented `wip\ActionsController`
 *     routes (`/get-approved`/`/get-trs-status`) on a hypothesis they
 *     mapped to this split -- switched away once it turned out neither
 *     route is mentioned anywhere in TRS's own extensive project
 *     documentation, while `approval_status` is confirmed as the real,
 *     actively-enforced mechanism there.
 * Everything still computed from these (% completion, estimates,
 * variance, Approved recovery Rate) stays "—"/0 pending further
 * column-by-column requests, rather than a fabricated number. Falls
 * back entirely to the original xlsx-faithful sample dataset
 * (src/data/sampleEngagementProgressingData.js) if the real SSS fetch
 * errors or there are no live engagements -- TRS data has no separate
 * fallback path, since it augments SSS-sourced rows rather than driving
 * which rows exist.
 *
 * Checking a checkbox in the Pending TRS / Approved TRS / Booked SSS
 * "Total Hours" cell for a row adds that row+category's per-staff
 * drill-down panel below the main table; checking more boxes stacks more
 * panels (in the order checked), unchecking removes just that one.
 */
import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { useBranchContext } from '../hooks/useBranchContext.js';
import {
  useEngagements,
  useClients,
  useStaffList,
  usePositionRates,
  useSchedules,
  scheduleEngagementId,
  scheduleStaffId,
  scheduleHours,
} from '../hooks/useReferenceData.js';
import {
  useTimeEntries,
  timeEntryUserId,
  timeEntryEngagementId,
  timeEntryHours,
  timeEntryStaffName,
  isChargeableClientEntry,
  isApprovedEntry,
  isPendingEntry,
} from '../hooks/useTrsTimeEntries.js';
import {
  sampleEngagementProgressingRows,
  STAFF_DETAIL as SAMPLE_STAFF_DETAIL,
} from '../data/sampleEngagementProgressingData.js';
import { EngagementProgressingGrid } from '../components/wip/EngagementProgressingGrid.jsx';
import { EngagementProgressingDetailPanel } from '../components/wip/EngagementProgressingDetailPanel.jsx';

/** Every non-identity PROGRESSING_COLUMNS key defaults to this until its real data source is wired. */
const PENDING_DEFAULTS = { hours: 0, charges: 0, na: 'NA' };
const PER_PAGE_OPTIONS = [10, 25, 50, 100];
const ENGAGEMENT_STATUS_LABELS = { planned: 'Planned', started: 'Started', completed: 'Completed' };

/** Builds a full 32-key row matching PROGRESSING_COLUMNS, with real SSS/TRS values where known. */
function buildLiveRow(engagement, index, {
  clientById, bookedHoursByEngagement, bookedChargesByEngagement,
  approvedHoursByEngagement, approvedChargesByEngagement,
  pendingHoursByEngagement, pendingChargesByEngagement,
}) {
  const id = String(engagement.id);
  const period =
    engagement.plan_start_date && engagement.plan_end_date
      ? `${engagement.plan_start_date} - ${engagement.plan_end_date}`
      : '\u2014';

  return {
    id: `live-${id}`,
    no: index + 1,
    clientName: clientById.get(String(engagement.client_id))?.name ?? 'Unknown Client',
    clientCode: clientById.get(String(engagement.client_id))?.code ?? '\u2014',
    engagementName: engagement.title ?? engagement.name ?? `Engagement #${id}`,
    engagementCode: engagement.code ?? '\u2014',
    period,
    // Filter-only fields (not part of PROGRESSING_COLUMNS, so they don't
    // render as their own grid column) -- the engagement's own
    // status/division, best-guess field names, distinct from the
    // 'status' column below (which is a fixed "period-end" marker, not
    // the engagement's lifecycle status).
    division: engagement.division ?? null,
    engagementStatus: engagement.status ?? null,
    pendingHours: Math.round((pendingHoursByEngagement.get(id) ?? 0) * 100) / 100,
    pendingCharges: Math.round((pendingChargesByEngagement.get(id) ?? 0) * 100) / 100,
    approvedHours: Math.round((approvedHoursByEngagement.get(id) ?? 0) * 100) / 100,
    approvedCharges: Math.round((approvedChargesByEngagement.get(id) ?? 0) * 100) / 100,
    bookedHours: Math.round((bookedHoursByEngagement.get(id) ?? 0) * 100) / 100,
    bookedCharges: Math.round((bookedChargesByEngagement.get(id) ?? 0) * 100) / 100,
    // "Approved budget" columns -- from the engagement's own Billing &
    // Budget fields. budget_hours/budget_fee are CONFIRMED real field
    // names (cross-checked against the Staff Audit Scheduler project's
    // own GRD export mapping work, which maps engagement_estimated_fees
    // <- budget_fee on this same Engagement model).
    budgetHours: Number(engagement.budget_hours ?? 0),
    budgetTotal: Number(engagement.budget_fee ?? 0),
    // "EL Approved -> Fee" -- from the engagement's "Budget EL" field.
    // ⚠️ budget_el is a BEST-GUESS field name (unlike budget_hours/
    // budget_fee above, this one hasn't been independently confirmed) --
    // following the same Billing & Budget naming pattern. First thing to
    // check if this column shows 0 for engagements that clearly have an
    // EL amount set.
    elApprovedFee: Number(engagement.budget_el ?? 0),
    approvedRecoveryRate: 0,
    pctCompleteSssHours: 0,
    pctCompleteSssCharges: 0,
    varianceSssHours: 0,
    varianceSssCharges: 0,
    estCompleteSssHours: 0,
    estCompleteSssCharges: 0,
    newEstimatedSss: PENDING_DEFAULTS.na,
    estAdditionalSss: PENDING_DEFAULTS.na,
    estimatedSss: PENDING_DEFAULTS.na,
    pctCompleteTrsFee: 0,
    pctCompleteTrsDate: 0,
    varianceTrsHours1: 0,
    varianceTrsCharges1: 0,
    varianceTrsHours2: 0,
    varianceTrsCharges2: 0,
    status: 'period-end',
  };
}

export const EngagementProgressingPage = () => {
  const { activeBranchId, setActiveBranchId, branches } = useBranchContext();
  const engagementsQuery = useEngagements(activeBranchId ? { branch_id: activeBranchId } : {});
  const clientsQuery = useClients();
  const staffQuery = useStaffList(activeBranchId ? { branch_id: activeBranchId } : {});
  const positionRatesQuery = usePositionRates();
  const schedulesQuery = useSchedules(activeBranchId ? { branchId: activeBranchId } : {});
  const timeEntriesQuery = useTimeEntries();

  const isLoading =
    engagementsQuery.isLoading || clientsQuery.isLoading || staffQuery.isLoading ||
    positionRatesQuery.isLoading || schedulesQuery.isLoading;
  const isError =
    engagementsQuery.isError || clientsQuery.isError || staffQuery.isError ||
    positionRatesQuery.isError || schedulesQuery.isError;
  // TRS deliberately excluded from the gates above -- a slow/failing TRS
  // call must not block the SSS-driven rows (confirmed reliable) from
  // rendering; Approved/Pending TRS columns just stay 0 until TRS
  // resolves, same as before this integration existed.
  const trsDataReady = !timeEntriesQuery.isError;

  const { liveRows, liveStaffDetail } = useMemo(() => {
    if (isLoading || isError) return { liveRows: [], liveStaffDetail: {} };

    const engagements = engagementsQuery.data ?? [];
    const clients = clientsQuery.data ?? [];
    const staff = staffQuery.data ?? [];
    const positionRates = positionRatesQuery.data ?? [];
    const schedules = schedulesQuery.data ?? [];
    const chargeableTimeEntries = trsDataReady
      ? (timeEntriesQuery.data ?? []).filter(isChargeableClientEntry)
      : [];

    const clientById = new Map(clients.map((c) => [String(c.id), c]));
    const staffById = new Map(staff.map((s) => [String(s.id), s]));

    const latestRateByPosition = new Map();
    [...positionRates]
      .sort((a, b) => new Date(a.effective_date) - new Date(b.effective_date))
      .forEach((r) => latestRateByPosition.set(r.position_title, Number(r.hourly_rate)));

    const rateFor = (staffId) => {
      const position = staffById.get(staffId)?.position;
      return position ? (latestRateByPosition.get(position) ?? 0) : 0;
    };

    const bookedHoursByEngagement = new Map();
    const bookedChargesByEngagement = new Map();
    const schedulesByEngagement = new Map();

    for (const s of schedules) {
      const engId = scheduleEngagementId(s);
      if (!engId) continue;
      const hours = scheduleHours(s);
      const staffId = scheduleStaffId(s);
      const charges = hours * rateFor(staffId);

      bookedHoursByEngagement.set(engId, (bookedHoursByEngagement.get(engId) ?? 0) + hours);
      bookedChargesByEngagement.set(engId, (bookedChargesByEngagement.get(engId) ?? 0) + charges);

      if (!schedulesByEngagement.has(engId)) schedulesByEngagement.set(engId, []);
      schedulesByEngagement.get(engId).push({ ...s, __hours: hours, __charges: charges, __staffId: staffId });
    }

    /**
     * Groups a subset of TRS entries (already filtered to approved or
     * pending) by engagement, computing per-row hours/charges the same
     * way as the SSS booked-hours pass above -- charges = hours x the
     * staff's SSS position rate (SSS and TRS share the same user table,
     * so a TRS entry's user_id should match an SSS staff record).
     * @param {any[]} entries
     */
    const groupTrsEntries = (entries) => {
      const hoursByEngagement = new Map();
      const chargesByEngagement = new Map();
      const entriesByEngagement = new Map();

      for (const entry of entries) {
        const engId = timeEntryEngagementId(entry);
        if (!engId) continue;
        const staffId = timeEntryUserId(entry);
        const hours = timeEntryHours(entry);
        const charges = hours * rateFor(staffId);

        hoursByEngagement.set(engId, (hoursByEngagement.get(engId) ?? 0) + hours);
        chargesByEngagement.set(engId, (chargesByEngagement.get(engId) ?? 0) + charges);

        if (!entriesByEngagement.has(engId)) entriesByEngagement.set(engId, []);
        entriesByEngagement.get(engId).push({ ...entry, __hours: hours, __charges: charges, __staffId: staffId });
      }

      return { hoursByEngagement, chargesByEngagement, entriesByEngagement };
    };

    const approved = groupTrsEntries(chargeableTimeEntries.filter(isApprovedEntry));
    const pending = groupTrsEntries(chargeableTimeEntries.filter(isPendingEntry));

    const rows = engagements.map((e, i) =>
      buildLiveRow(e, i, {
        clientById,
        bookedHoursByEngagement,
        bookedChargesByEngagement,
        approvedHoursByEngagement: approved.hoursByEngagement,
        approvedChargesByEngagement: approved.chargesByEngagement,
        pendingHoursByEngagement: pending.hoursByEngagement,
        pendingChargesByEngagement: pending.chargesByEngagement,
      })
    );

    /**
     * Builds a detail-panel staff list from grouped TRS entries.
     * @param {any[]} entries
     * @param {string} statusLabel
     */
    const trsStaffDetail = (entries, statusLabel) =>
      entries.map((entry) => {
        const staffRow = staffById.get(entry.__staffId);
        return {
          name: timeEntryStaffName(entry) ?? staffRow?.display_name ?? staffRow?.full_name ?? staffRow?.name ?? `Staff #${entry.__staffId}`,
          rate: entry.__hours > 0 ? Math.round((entry.__charges / entry.__hours) * 100) / 100 : rateFor(entry.__staffId),
          hours: Math.round(entry.__hours * 100) / 100,
          charges: Math.round(entry.__charges * 100) / 100,
          status: statusLabel,
        };
      });

    const staffDetail = {};
    rows.forEach((row) => {
      const engId = row.id.replace('live-', '');
      const rowSchedules = schedulesByEngagement.get(engId) ?? [];
      staffDetail[`${row.id}:booked`] = rowSchedules.map((s) => {
        const staffRow = staffById.get(s.__staffId);
        return {
          name: staffRow?.display_name ?? staffRow?.full_name ?? staffRow?.name ?? `Staff #${s.__staffId}`,
          rate: rateFor(s.__staffId),
          hours: Math.round(s.__hours * 100) / 100,
          charges: Math.round(s.__charges * 100) / 100,
          status: 'Chargable',
        };
      });
      staffDetail[`${row.id}:approved`] = trsStaffDetail(approved.entriesByEngagement.get(engId) ?? [], 'approved');
      staffDetail[`${row.id}:pending`] = trsStaffDetail(pending.entriesByEngagement.get(engId) ?? [], 'pending');
    });

    return { liveRows: rows, liveStaffDetail: staffDetail };
  }, [
    isLoading, isError, trsDataReady, engagementsQuery.data, clientsQuery.data, staffQuery.data,
    positionRatesQuery.data, schedulesQuery.data, timeEntriesQuery.data,
  ]);

  const usingSampleData = !isLoading && (isError || liveRows.length === 0);
  const allRows = usingSampleData ? sampleEngagementProgressingRows : liveRows;
  const staffDetailMap = usingSampleData ? SAMPLE_STAFF_DETAIL : liveStaffDetail;

  const [search, setSearch] = useState('');
  const [divisionFilter, setDivisionFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(PER_PAGE_OPTIONS[0]);
  const resetToFirstPage = () => setPage(1);

  // Array (not Set) so insertion order is preserved -- "select box 1 show
  // one, select box 2 show two" implies panels stack in the order checked.
  const [selectedKeys, setSelectedKeys] = useState(/** @type {string[]} */ ([]));

  const divisionOptions = useMemo(
    () => [...new Set(allRows.map((r) => r.division).filter(Boolean))].sort(),
    [allRows]
  );
  const statusOptions = useMemo(
    () => [...new Set(allRows.map((r) => r.engagementStatus).filter(Boolean))].sort(),
    [allRows]
  );

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allRows.filter((r) => {
      if (divisionFilter && r.division !== divisionFilter) return false;
      if (statusFilter && r.engagementStatus !== statusFilter) return false;
      if (!q) return true;
      return (
        r.clientName.toLowerCase().includes(q) ||
        r.engagementName.toLowerCase().includes(q) ||
        String(r.clientCode).toLowerCase().includes(q) ||
        String(r.engagementCode).toLowerCase().includes(q)
      );
    });
  }, [allRows, search, divisionFilter, statusFilter]);

  const selectedKeySet = useMemo(() => new Set(selectedKeys), [selectedKeys]);

  const toggle = (rowId, category) => {
    const key = `${rowId}:${category}`;
    setSelectedKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const rowsById = useMemo(() => new Map(allRows.map((r) => [r.id, r])), [allRows]);

  const total = filteredRows.length;
  const lastPage = Math.max(1, Math.ceil(total / perPage));
  const clampedPage = Math.min(page, lastPage);
  const pageRows = useMemo(
    () => filteredRows.slice((clampedPage - 1) * perPage, clampedPage * perPage),
    [filteredRows, clampedPage, perPage]
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-primary">
            Work in progress summary period-ended engagements report
          </h1>
          <p className="text-sm text-muted">
            Pending/Approved TRS time charges vs. SSS booked hours, per engagement.
          </p>
        </div>
        {usingSampleData ? (
          <span className="rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 text-xs font-medium text-amber-500">
            Sample data{isError ? ' \u2014 live fetch failed' : ' \u2014 no live engagements for this filter'}
          </span>
        ) : (
          <span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-500">
            Live from SSS{trsDataReady ? ' & TRS' : ' \u2014 TRS unreachable, Pending/Approved TRS columns show 0'}
          </span>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-edge bg-surface p-3">
        <div className="flex items-center gap-2 rounded-md border border-edge bg-base px-3 py-1.5 sm:w-72">
          <Search className="h-4 w-4 text-muted" />
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              resetToFirstPage();
            }}
            placeholder="Search engagement..."
            className="w-full bg-transparent text-sm text-primary placeholder:text-muted focus:outline-none"
          />
        </div>

        <select
          value={activeBranchId ?? ''}
          onChange={(e) => {
            setActiveBranchId(e.target.value || null);
            resetToFirstPage();
          }}
          className="rounded-md border border-edge bg-base px-3 py-1.5 text-sm text-primary focus:border-accent focus:outline-none"
        >
          <option value="">All Branches</option>
          {branches.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>

        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            resetToFirstPage();
          }}
          className="rounded-md border border-edge bg-base px-3 py-1.5 text-sm text-primary focus:border-accent focus:outline-none"
        >
          <option value="">All Statuses</option>
          {statusOptions.map((s) => (
            <option key={s} value={s}>
              {ENGAGEMENT_STATUS_LABELS[s] ?? s}
            </option>
          ))}
        </select>

        <select
          value={divisionFilter}
          onChange={(e) => {
            setDivisionFilter(e.target.value);
            resetToFirstPage();
          }}
          className="rounded-md border border-edge bg-base px-3 py-1.5 text-sm text-primary focus:border-accent focus:outline-none"
        >
          <option value="">All Divisions</option>
          {divisionOptions.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>

        {(statusFilter || divisionFilter || search) && (
          <button
            type="button"
            onClick={() => {
              setStatusFilter('');
              setDivisionFilter('');
              setSearch('');
              resetToFirstPage();
            }}
            className="text-sm text-muted hover:text-primary"
          >
            Clear filters
          </button>
        )}

        <span className="ml-auto text-sm text-muted">{total} engagement{total === 1 ? '' : 's'}</span>
      </div>

      <EngagementProgressingGrid rows={pageRows} selectedKeys={selectedKeySet} onToggle={toggle} />

      {total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-muted">
          <div className="flex items-center gap-2">
            <span>Rows per page</span>
            <select
              value={perPage}
              onChange={(e) => {
                setPerPage(Number(e.target.value));
                resetToFirstPage();
              }}
              className="rounded-md border border-edge bg-base px-2 py-1 text-sm text-primary focus:border-accent focus:outline-none"
            >
              {PER_PAGE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-3">
            <span>
              Showing page {clampedPage} of {lastPage} ({total} rows)
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={clampedPage <= 1}
                onClick={() => setPage(Math.max(1, clampedPage - 1))}
                className="rounded border border-edge px-3 py-1 disabled:opacity-40"
              >
                Prev
              </button>
              <button
                type="button"
                disabled={clampedPage >= lastPage}
                onClick={() => setPage(Math.min(lastPage, clampedPage + 1))}
                className="rounded border border-edge px-3 py-1 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedKeys.length > 0 && (
        <div className="flex flex-col gap-4">
          {selectedKeys.map((key) => {
            const [rowId, category] = key.split(':');
            const row = rowsById.get(rowId);
            if (!row) return null;
            return (
              <EngagementProgressingDetailPanel
                key={key}
                category={category}
                row={row}
                staff={staffDetailMap[key] ?? []}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};
